import { useEffect } from 'react';
import {
  StackLayout,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';
import { useIntl } from '@leagueplatform/locales';
import { BaseModalContainer } from 'components/modals/common/base-modal.component';
import { ModalBody } from 'components/modals/common/modal-body.component';
import { useDeleteAccount } from 'hooks/mutations/use-delete-account.hook';

import deleteAccountWarning from 'assets/delete-account-warning.svg';
import errorSubtleIcon from 'assets/error-subtle-icon.svg';
import { ErrorState } from 'components/error-state/error-state.component';
import { ERROR_TYPES } from 'hooks/use-error-content/use-error-content.hook';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import {
  ANALYTICS_DETAILS,
  QUERY_PARAM_KEYS,
  QUERY_PARAM_MODAL_VALUE,
} from 'common/constants';
import { LoadingSpinner } from 'components/loading-spinner/loading-spinner.component';
import { useGetUserProfile } from 'hooks/queries/use-get-user-profile.hook';
import { ModalActionButton } from './common/modal-action-button.component';

export const DeleteAccountModal = ({ onClose }: { onClose: () => void }) => {
  const { formatMessage } = useIntl();
  useEffect(() => {
    trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
    });
  }, []);

  const isMobile = useMediaQuery(queryHelpers.only('mobile'));
  const {
    mutateAsync,
    isLoading: isAccountDeleteLoading,
    isError,
    reset,
  } = useDeleteAccount();
  const { data, isSuccess, isLoading, error } = useGetUserProfile();

  if (isLoading) {
    return (
      <BaseModalContainer>
        <LoadingSpinner />
      </BaseModalContainer>
    );
  }

  if (!isSuccess || isError) {
    return (
      <BaseModalContainer>
        <ErrorState
          error={error}
          imageSrc={errorSubtleIcon}
          buttonAction={reset}
          errorType={ERROR_TYPES.API_ERROR}
        />
      </BaseModalContainer>
    );
  }

  const { userId } = data;
  const handleDeleteAccount = async () => {
    await mutateAsync(userId, {
      onSuccess: () => {
        const queryParam = new URLSearchParams();
        queryParam.set(
          QUERY_PARAM_KEYS.MODAL,
          QUERY_PARAM_MODAL_VALUE.DELETE_ACCOUNT_DISCLAIMER,
        );
        onClose();
        StandaloneAuth.client.logout({
          federated: true,
          returnTo: `${window.location.origin}?${queryParam.toString()}`,
        });
      },
    });
  };

  return (
    <BaseModalContainer
      contentFooterSpacing="$twoAndHalf"
      footer={
        <StackLayout
          horizontalAlignment={{ '@initial': 'end', '@mobile': 'center' }}
          orientation={{
            '@initial': 'horizontal',
            '@mobile': 'vertical',
          }}
          spacing="$one"
          css={{
            flexGrow: 0,
            '@mobile': {
              flexFlow: 'column-reverse',
            },
          }}
        >
          <ModalActionButton
            actionType="cancel"
            buttonText={formatMessage({ id: 'STR_NEVER_MIND' })}
            isMobile={isMobile}
            css={{ color: '$secondaryTextDefault' }}
            trackAnalytics={{
              product_area: PRODUCT_AREA.SETTINGS,
              screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
              detail: ANALYTICS_DETAILS.CANCEL,
            }}
          />
          <ModalActionButton
            actionType="submit"
            onClick={handleDeleteAccount}
            buttonText={formatMessage({ id: 'STR_YES_DELETE_ACCOUNT' })}
            isMobile={isMobile}
            destructive
            loading={isAccountDeleteLoading}
            disabled={isAccountDeleteLoading}
            trackAnalytics={{
              product_area: PRODUCT_AREA.SETTINGS,
              screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
              detail: ANALYTICS_DETAILS.CONFIRM_DELETION,
            }}
          />
        </StackLayout>
      }
    >
      <ModalBody
        image={deleteAccountWarning}
        heading={formatMessage({ id: 'STR_DELETE_ACCOUNT_HEADING' })}
        paragraphs={[
          formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_1' }),
          formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_2' }),
          formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_3' }),
          formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_4' }),
        ]}
      />
    </BaseModalContainer>
  );
};
